import {
  APP_ROUTES,
  DEFAULT_ERROR_MESSAGE,
  FlyerType,
  SubscriptionTypeEnum,
  ViewMode,
} from '@/config/constants';
import { EventDetails, TrackingEvents, TrackingStage } from '@/config/tracking';
import AuthenticationContext from '@/context/AuthenticationContext';
import ScrollRestorationContext from '@/context/ScrollRestorationContext';
import {
  BodyMediumEmphasized,
  ButtonOutlineMedium,
  ButtonOutlineSmall,
  CommunityTag,
  ExpiredTag,
  ExtendedCommunityTag,
  SchoolTag,
  Tag,
  TitleSmallEmphasized,
  useSnackbar,
} from '@/design-system';
import { SAVE_FLYER_MUTATION } from '@/graphql/mutation';
import { districtVar, openLoginDrawerVar } from '@/graphql/state';
import { CallToAction, Page, SaveFlyerResponse, TitleTranslations } from '@/graphql/types';
import { useLiveFeedback, useMetrics, useNavigateWithTracking } from '@/hooks';
import { useAnalytics } from '@/hooks/useAnalytics';
import { clearSavedFlyersCache } from '@/pages';
import { FlyerStatus } from '@/types/flyer';
import { GenericTrackTypes } from '@/types/genericTrackTypes';
import { useApolloClient, useMutation, useReactiveVar } from '@apollo/client';
import { cx } from '@emotion/css';
import { useContext, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AccessibleDivButton } from '../AccessibleDivButton';
import { FavoriteIcon } from '../FavoriteIcon';
import { LiveFeedback } from '../LiveFeedback';
import styles from './FlyerCard.style';
import UserPreferencesContext from '@/context/UserPreferencesContext';
import { FlyerCardSkeleton } from './FlyerCardSkeleton';
import { CTAMapper } from '@/pages/FlyerDetails/CtaMapper';
import getValidHref from '@/utils/getValidHref';
import config from '@/config/config';
import { useIsMobile } from '@/hooks';
import { FlyerCardSwiper } from './FlyerCardSwiper';
import { useViewModeContext } from '@/context/ViewModeContext';
import { useDwellInView } from '@/hooks/useDwellInView';

interface FlyerCardProps {
  id: number;
  thumbnail: string;
  title: string;
  isFavorite: boolean;
  trackId: string;
  inactive?: boolean;
  status?: FlyerStatus;
  isRead?: boolean;
  audienceId?: string;
  districtId?: string | null;
  flyerType?: string;
  trackingEventFrom: string;
  isExpandedCommunity?: boolean;
  isCommunityFree?: boolean;
  showTag?: boolean;
  titleTranslations?: TitleTranslations;
  ctas?: CallToAction[];
  pages?: Page[];
  isEmbedded?: boolean;
  distributionsReleased?: number;
}

const FlyerTag = ({
  flyerType,
  isExpandedCommunity,
  isCommunityFree,
}: {
  flyerType?: string;
  isExpandedCommunity: boolean;
  isCommunityFree: boolean;
}) => {
  if (flyerType === FlyerType.school) {
    return <SchoolTag />;
  } else {
    return (
      <div className={styles.communityTag}>
        {isExpandedCommunity ? <ExtendedCommunityTag /> : <CommunityTag />}
        {isCommunityFree && <Tag variant="free">Free</Tag>}
      </div>
    );
  }
};

interface FlyerCTAButtonProps {
  ctas: CallToAction[];
  flyerTitle: string;
  isListMode: boolean;
  trackCTAButton?: (
    buttonName: string,
    link: string,
    type: string,
    featureId?: string | null
  ) => void;
}

const FlyerCTAButton: React.FC<FlyerCTAButtonProps> = ({
  ctas,
  trackCTAButton,
  isListMode,
  flyerTitle,
}) => {
  const primaryCTA = useMemo(() => ctas[0], [ctas]);

  if (!primaryCTA) return null;
  const { type, value, id } = primaryCTA;
  const ctaButton = CTAMapper[type];
  if (!ctaButton) return null;
  const isMobile = useIsMobile();

  const Button = !isMobile && isListMode ? ButtonOutlineMedium : ButtonOutlineSmall;

  return (
    <div className={styles.ctaButton}>
      <Button
        fullWidth
        onClick={() => {
          if (trackCTAButton) trackCTAButton(ctaButton.label, value, type, id);
          const link = getValidHref(value, type);
          const target = ['email', 'call'].includes(type) ? '_self' : '_blank';
          window.open(link, target);
        }}
        aria-label={`${ctaButton.label} ${flyerTitle} button`}
      >
        {ctaButton.label}
      </Button>
    </div>
  );
};

export const FlyerCard: React.FC<FlyerCardProps> = ({
  id,
  thumbnail,
  title,
  isFavorite,
  trackId,
  inactive = false,
  status,
  isRead = false,
  audienceId,
  districtId: providedDistrictId,
  flyerType,
  trackingEventFrom,
  titleTranslations,
  isExpandedCommunity = false,
  isCommunityFree = false,
  showTag = false,
  ctas = [],
  pages = [],
  isEmbedded = false,
  distributionsReleased,
}) => {
  const client = useApolloClient();
  const { saveScrollY } = useContext(ScrollRestorationContext);
  const { language } = useContext(UserPreferencesContext);

  const district = useReactiveVar(districtVar);
  const districtId = providedDistrictId ?? district?.districtId;
  const { track } = useAnalytics();
  const showToast = useSnackbar();
  const navigateWithTracking = useNavigateWithTracking();
  const [saveFlyer] = useMutation<SaveFlyerResponse>(SAVE_FLYER_MUTATION);
  const [loadingFlyer, setLoadingFlyer] = useState(true);
  const [feedbackMessage, setFeedbackMessage] = useLiveFeedback({ delay: 2000 });
  const { trackMetrics } = useMetrics();
  const { trackEvent } = useAnalytics();
  const { search } = useLocation();
  const navigate = useNavigate();
  const translatedTitle = titleTranslations ? titleTranslations[language.code] : undefined;
  const flyerTitle = translatedTitle ?? title;
  const audienceType = new URLSearchParams(search).get('audienceType');
  const { viewMode } = useViewModeContext();
  const isListMode = viewMode === 'list';
  const isExpired = status === FlyerStatus.expired;
  const isMobile = useIsMobile();

  const FlyerTitle = isListMode ? TitleSmallEmphasized : BodyMediumEmphasized;

  const onLoadFlyer = () => {
    setLoadingFlyer(false);
  };

  const isSchool = flyerType === FlyerType.school && !!audienceId && audienceId !== 'all';
  const resourceId = isSchool ? audienceId : districtId ?? '';
  const resource = isSchool ? 'schools' : 'districts';
  const screenReaderTitle = `${
    flyerType === FlyerType.school ? 'School' : 'Community'
  } Flyer ${flyerTitle}`;

  const { isAuthenticated } = useContext(AuthenticationContext);

  // Track view when the flyer image remains 100% visible for > 3 seconds in list view
  const imageContainerRef = useRef<HTMLDivElement | null>(null);
  useDwellInView(imageContainerRef, {
    enabled: isListMode && !loadingFlyer,
    threshold: isMobile ? 1 : 0.5,
    dwellMs: 3000,
    once: true,
    tolerance: 0.01,
    requireScroll: true,
    onDwell: () => {
      trackMetrics({
        flyerId: id.toString(),
        metric: 'viewflyer',
        action: 'pa-dwell-viewflyer',
        resource,
        resourceId,
        audienceId,
        distributionsReleased,
      });
    },
  });

  const onClickFavoriteFlyer = async () => {
    if (!isAuthenticated) {
      openLoginDrawerVar(true);
      return;
    }
    try {
      client.cache.modify({
        id: `Flyer:{"flyerId":${id}}`,
        fields: {
          isFavorite: () => !isFavorite,
        },
      });
      const { data } = await saveFlyer({
        variables: {
          input: {
            flyerId: id,
            isFavorite: !isFavorite,
          },
        },
      });
      if (data?.saveFlyer.flyerId) {
        const action = isFavorite ? 'pa-unsave' : 'pa-save';
        track({
          category: `${trackId}:${isFavorite ? 'unfavorite' : 'favorite'}_flyer`,
          action: GenericTrackTypes.BUTTON_PRESSED,
          label: title,
          value: id,
        });
        trackMetrics({
          flyerId: id.toString(),
          action,
          metric: 'thumbnail-save',
          resource,
          resourceId,
          audienceId,
          distributionsReleased,
        });

        const flyerSet = 'flyer_set';
        const eventFrom = {
          stage_from: TrackingStage.discovery,
          event_from: TrackingEvents.viewFlyerSet,
          event_detail_from: trackingEventFrom,
        };
        trackEvent({
          stage: TrackingStage.consideration,
          event: isFavorite ? TrackingEvents.unsaveFlyer : TrackingEvents.saveFlyer,
          event_detail: isFavorite
            ? EventDetails.unsaveFlyer(flyerSet)
            : EventDetails.saveFlyer(flyerSet),
          ...eventFrom,
        });
        if (!isFavorite) {
          setFeedbackMessage('Flyer added to favorites');
          showToast({
            message: 'The flyer has been saved.',
            buttonLabel: 'View Saved',
            onClick: () => navigateWithTracking(APP_ROUTES.saved, eventFrom),
          });
        } else {
          setFeedbackMessage('Flyer removed from favorites');
          showToast({
            message: 'Flyer unsaved.',
          });
        }
        clearSavedFlyersCache(client.cache);
      }
    } catch (err) {
      showToast({ message: DEFAULT_ERROR_MESSAGE });
    }
  };

  const flyerUrl = useMemo(() => {
    const queryParams = new URLSearchParams();
    if (audienceId && audienceType !== SubscriptionTypeEnum.district) {
      queryParams.append('audienceId', audienceId);
    }

    const queryString = queryParams.toString();
    return `${isEmbedded ? '/embed' : ''}/district/${districtId}/flyer/${id}${
      queryString ? `?${queryString}` : ''
    }`;
  }, [id, districtId, audienceId, audienceType, isEmbedded]);

  const trackingState = {
    event_from: TrackingEvents.viewFlyerSet,
    event_detail_from: trackingEventFrom,
    stage_from: TrackingStage.discovery,
  };

  const onFlyerClick = () => {
    track({
      action: GenericTrackTypes.LINK_PRESSED,
      category: `${trackId}:go_to_flyer_details`,
      label: title,
      value: Number(id),
    });

    // Temporarily(?) disabling this metrics event
    // trackMetrics({
    //   flyerId: id.toString(),
    //   metric: 'viewflyer',
    //   action: 'thumbnail-view',
    //   resource,
    //   resourceId,
    //   distributionsReleased,
    // });

    saveScrollY(window.pageYOffset);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === ' ') {
      e.preventDefault();
      onFlyerClick();
      navigate(flyerUrl, { state: trackingState });
    }
  };

  const FlyerCardLink = ({ children }: { children: React.ReactNode }) => (
    <Link
      to={flyerUrl}
      state={trackingState}
      onClick={onFlyerClick}
      className={styles.link}
      onKeyDown={handleKeyDown}
      aria-label={screenReaderTitle}
    >
      {children}
    </Link>
  );

  return (
    <>
      {loadingFlyer && (
        <FlyerCardSkeleton
          showFavoriteButton={!isEmbedded}
          showTag={showTag}
          hasCTA={ctas.length > 0}
        />
      )}
      <div>
        <div
          className={cx(styles.flyerCard, inactive ? styles.inactiveFlyer : {})}
          key={id}
          style={loadingFlyer ? { display: 'none' } : {}}
          data-testid="flyerCard"
        >
          {isRead && (
            <div className={styles.viewedTag}>
              <Tag
                variant="viewed"
                aria-label="Viewed"
                aria-describedby={`viewed-description-${id}`}
              >
                Viewed
              </Tag>
              <div id={`viewed-description-${id}`} className="sr-only">
                This flyer has been viewed.
              </div>
            </div>
          )}
          <div className={styles.imageContainer} ref={imageContainerRef}>
            <h2 className={styles.imageHeading}>
              <FlyerCardLink>
                <FlyerCardSwiper
                  id={id}
                  districtId={districtId}
                  pages={pages}
                  thumbnail={thumbnail}
                  flyerTitle={flyerTitle}
                  isListMode={isListMode}
                  onLoadFlyer={onLoadFlyer}
                  isExpired={isExpired}
                />
              </FlyerCardLink>
            </h2>
          </div>
          <div className={styles.flyerContent}>
            <FlyerCardLink>
              <FlyerTitle data-testid="flyerCardTitle" className={styles.flyerTitle} nonTranslate>
                {flyerTitle}
              </FlyerTitle>
            </FlyerCardLink>
            {!isEmbedded && (
              <AccessibleDivButton
                onClick={onClickFavoriteFlyer}
                data-testid="flyerCardFavoriteButton"
                aria-label={
                  isFavorite
                    ? `Remove ${flyerTitle} from favorites`
                    : `Add ${flyerTitle} to favorites`
                }
                aria-pressed={isFavorite}
                role="button"
              >
                <FavoriteIcon isFavorite={isFavorite} size={isListMode ? 28 : 24} />
              </AccessibleDivButton>
            )}
          </div>
        </div>
        {showTag || isExpired ? (
          <div
            className={styles.tagContainer({ showTag, isGrid: !isListMode })}
            style={loadingFlyer ? { display: 'none' } : {}}
          >
            {showTag && (
              <FlyerTag
                flyerType={flyerType}
                isCommunityFree={isCommunityFree}
                isExpandedCommunity={isExpandedCommunity}
              />
            )}
            {isExpired && <ExpiredTag />}
          </div>
        ) : null}
        {!loadingFlyer && !isExpired && (
          <FlyerCTAButton ctas={ctas} isListMode={isListMode} flyerTitle={flyerTitle} />
        )}
      </div>
      <LiveFeedback message={feedbackMessage} />
    </>
  );
};
